/* eslint-disable camelcase */
import * as t from 'io-ts';

import {RCompoundConditions} from '../../settings/editors/conditions/compoundConditionModel';
import {makeApiType, makeEnumRuntimeType} from './apiTypes';
import {DataFieldsTableModel} from './dataFieldsTableModel';
import {RuleBundleTypesEnum} from './ruleCommonTypes';
import {parseRule, RRuleBase, RRuleBundleBase, RuleBaseModelStrict, serializeRule} from './ruleModel';

const RRuleBundleRawDefinitionRequiredTagging = t.type({
  conditions: RCompoundConditions,
  requiredTaggingParams: t.readonlyArray(
    t.type({
      tagListName: t.string,
      isOneTagRequired: t.boolean,
      tagIds: t.readonlyArray(t.Integer),
    }),
  ),
});

const RRuleBundleRawDefinition = RRuleBundleRawDefinitionRequiredTagging;

/*
 * Runtime type.
 */

/*
 * Rule bundle model depends if its fetched directly or within rule.
 * When it's fetched directly, it carries rule  properties under
 * the `rules` attribute. But when it's fetched from the rule,
 * it doesnt. This is why we have a base model that is without the relationship.
 */

const RRuleBundle = t.intersection([
  RRuleBundleBase,
  t.readonly(
    t.type({
      rules: t.readonlyArray(RRuleBase),
    }),
  ),
  t.partial({
    rawDefinition: RRuleBundleRawDefinition,
  }),
]);

const RRuleBundles = t.readonlyArray(t.readonly(RRuleBundle));

const RRuleBundleSearch = t.type({
  total: t.number,
  limit: t.number,
  items: t.readonlyArray(RRuleBundle),
});

/*
 * Type.
 */

interface RuleBundleRawDefinition extends t.TypeOf<typeof RRuleBundleRawDefinition> {}

export interface RuleBundleBaseModel extends t.TypeOf<typeof RRuleBundleBase> {}
export interface RuleBundleModel extends t.TypeOf<typeof RRuleBundle> {}
export interface RuleBundleModelStrict extends t.TypeOf<typeof RRuleBundle> {
  rules: ReadonlyArray<RuleBaseModelStrict>;
}
export interface RuleBundleSearchModel extends t.TypeOf<typeof RRuleBundleSearch> {}

export const RRuleBundleType = makeEnumRuntimeType(RuleBundleTypesEnum);

export enum RuleBundlePrioritiesEnum {
  DEFAULT = 100,
  AFTER = 200,
}

/*
 * IO methods.
 */

function parseRawDefinition(src: any, ruleBundleType: unknown): RuleBundleRawDefinition | undefined {
  switch (ruleBundleType) {
    case RuleBundleTypesEnum.REQUIRED_TAGGING:
      return {
        conditions: src.conditions,
        requiredTaggingParams:
          src.required_tagging_params?.map((requiredTaggingParam: any) => ({
            tagListName: requiredTaggingParam.tag_list_name,
            isOneTagRequired: requiredTaggingParam.one_tag_required,
            tagIds: requiredTaggingParam.tag_ids,
          })) ?? [],
      };

    default:
      return undefined;
  }
}

export function parseRuleBundle(src: any): RuleBundleModel {
  return {
    id: src.id,
    name: src.name,
    namespace: src.namespace,
    priority: src.priority,
    sortOrder: src.sort_order,
    type: src.type,
    rules: src.rules?.map(parseRule) ?? [],
    rawDefinition: src.raw_definition ? parseRawDefinition(src.raw_definition, src.type) : undefined,
    url: src.url,
    dataFieldsTableId: src.data_fields_table_id,
  };
}

export function serializeRuleBundle(src: RuleBundleModelStrict, table?: DataFieldsTableModel): any {
  return {
    id: src.id,
    name: src.name,
    namespace: src.namespace,
    priority: src.priority,
    sort_order: src.sortOrder,
    type: src.type,
    rules: src.rules.map(serializeRule),
    data_fields_table:
      table?.rows.length && table?.columns.length
        ? {
            ...table,
            namespace: src.namespace,
            name: `Rule ${src.name}`,
          }
        : undefined,
  };
}

export function parseRuleBundles(src: any): ReadonlyArray<RuleBundleModelStrict> {
  return src.rule_bundles.map(parseRuleBundle);
}

export function parseRuleBundleSearch(src: any) {
  return {
    limit: src.limit,
    items: src.rule_bundles.map(parseRuleBundle),
    total: src.total,
  };
}

/*
 * API type.
 */

export const ApiRuleBundle = makeApiType('ruleBundle', RRuleBundle, parseRuleBundle);
export const ApiRuleBundles = makeApiType('ruleBundles', RRuleBundles, parseRuleBundles);
export const ApiRuleBundlesSearch = makeApiType('ruleBundles', RRuleBundleSearch, parseRuleBundleSearch);
